import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import iconFlash from "../../assets/icon-flash.svg";
import { AddToQueueIcon } from "../shared/AddToQueueIcon";
import { GitBranchIcon } from "../shared/GitBranchIcon";
import { RunAiAssessmentMenuButton } from "../RegulationsTable/RunAiAssessmentMenuButton";
import { StartAutoTaggingDialog } from "../RegulationsTable/AutoTagging";
import {
  normalizeRegulationVersion,
  type AutoTaggingBundleTaskRegulation,
} from "../../services/autoTaggingService";
import {
  selectAssessmentSidebarIds,
  useAppStore,
} from "../../stores";
import { useHasPrivilege } from "../../hooks/useHasPrivilege";

const MORE_ACTIONS_BUTTON_SX = {
  bgcolor: "#f5f5f5 !important",
  color: "#262626 !important",
  boxShadow: "none",
  border: "none",
  "&:hover": {
    bgcolor: "#e8e8e8 !important",
    boxShadow: "none",
  },
  textTransform: "none" as const,
  fontWeight: 500,
  px: 1.5,
  height: "32px",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit !important",
  },
};

const MORE_ACTIONS_MENU_PAPER_SX = {
  mt: 0.5,
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  minWidth: 220,
  "& .MuiMenuItem-root": {
    fontSize: "14px",
    py: 1,
  },
  "& .MuiListItemIcon-root": {
    minWidth: "32px !important",
    color: "#595959",
  },
};

type MoreActionKey = "addToQueue" | "manualAssessment" | "autoTagging";

export type RegulationDetailsSummaryActionsProps = Readonly<{
  regulationId: string | null;
  versionLabel: string;
  canExecuteAutoImpact: boolean;
  canReadChecklist: boolean;
  canExecuteManualAssessment: boolean;
  maxRegulationsPerBatch: number;
  companyScopeStarting: boolean;
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  onStartManualAssessment: () => void;
  onQueueLimitExceeded: () => void;
}>;

export function RegulationDetailsSummaryActions({
  regulationId,
  versionLabel,
  canExecuteAutoImpact,
  canReadChecklist,
  canExecuteManualAssessment,
  maxRegulationsPerBatch,
  companyScopeStarting,
  onCompanyScopeImpact,
  onCustomScopeImpact,
  onStartManualAssessment,
  onQueueLimitExceeded,
}: RegulationDetailsSummaryActionsProps) {
  const assessmentSidebarIds = useAppStore(selectAssessmentSidebarIds);
  const addAssessmentSidebarId = useAppStore(
    (state) => state.addAssessmentSidebarId,
  );
  const canRunSystemImpact = useHasPrivilege(
    "gri.assessment.impact.system.execute",
  );
  /** Same gate as list Bulk Action / row → Add to Assessment Queue. */
  const canShowAddToAssessmentQueuePrivilege = useHasPrivilege(
    [
      "gri.assessment.impact.execute",
      "gri.assessment.impact.system.execute",
    ],
    "any",
  );
  const canExecuteAutoTagging = useHasPrivilege("gri.auto_tagging.execute");

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] =
    useState<null | HTMLElement>(null);
  const [autoTaggingOpen, setAutoTaggingOpen] = useState(false);

  const isAlreadyQueued = Boolean(
    regulationId && assessmentSidebarIds.includes(regulationId),
  );
  const isAtQueueLimit = assessmentSidebarIds.length >= maxRegulationsPerBatch;
  const canShowAddToQueue =
    Boolean(regulationId) && canShowAddToAssessmentQueuePrivilege;
  const canAddToQueue =
    canShowAddToQueue && !isAlreadyQueued && !isAtQueueLimit;

  const autoTaggingRegulations = useMemo<AutoTaggingBundleTaskRegulation[]>(() => {
    if (!regulationId) return [];
    return [
      {
        regulation_id: regulationId,
        version: normalizeRegulationVersion(versionLabel),
      },
    ];
  }, [regulationId, versionLabel]);
  const canShowManualAssessment = canExecuteManualAssessment;
  const canShowAutoTagging =
    Boolean(regulationId) && canExecuteAutoTagging;

  const visibleMoreActions = useMemo(() => {
    const actions: MoreActionKey[] = [];
    if (canShowAddToQueue) actions.push("addToQueue");
    if (canShowManualAssessment) actions.push("manualAssessment");
    if (canShowAutoTagging) actions.push("autoTagging");
    return actions;
  }, [canShowAddToQueue, canShowAutoTagging, canShowManualAssessment]);

  const useMoreActionsMenu = visibleMoreActions.length >= 2;
  const singleAction =
    visibleMoreActions.length === 1 ? visibleMoreActions[0] : null;

  const handleMoreActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchorEl(event.currentTarget);
  };

  const handleCloseMoreActions = () => {
    setMoreActionsAnchorEl(null);
  };

  const handleAddToQueue = () => {
    if (!regulationId || !canAddToQueue) {
      if (isAtQueueLimit) onQueueLimitExceeded();
      handleCloseMoreActions();
      return;
    }
    const versionForStore = versionLabel?.trim()
      ? versionLabel.startsWith("v") || versionLabel.startsWith("V")
        ? versionLabel
        : `v${versionLabel}`
      : undefined;
    addAssessmentSidebarId(
      regulationId,
      versionForStore,
      maxRegulationsPerBatch,
    );
    handleCloseMoreActions();
  };

  const handleStartManualAssessment = () => {
    onStartManualAssessment();
    handleCloseMoreActions();
  };

  const handleOpenAutoTagging = () => {
    setAutoTaggingOpen(true);
    handleCloseMoreActions();
  };

  const addToQueueIcon = <AddToQueueIcon sx={{ fontSize: "16px" }} />;
  const manualAssessmentIcon = <GitBranchIcon sx={{ fontSize: "16px" }} />;
  const autoTaggingIcon = (
    <Box
      component="img"
      src={iconFlash}
      alt=""
      sx={{ width: 16, height: 16, display: "block" }}
    />
  );

  const renderStandaloneAction = (action: MoreActionKey) => {
    if (action === "addToQueue") {
      return (
        <Button
          variant="contained"
          size="small"
          startIcon={addToQueueIcon}
          onClick={handleAddToQueue}
          disabled={!canAddToQueue}
          sx={MORE_ACTIONS_BUTTON_SX}
        >
          Add to Assessment Queue
        </Button>
      );
    }
    if (action === "manualAssessment") {
      return (
        <Button
          variant="contained"
          size="small"
          startIcon={manualAssessmentIcon}
          onClick={handleStartManualAssessment}
          sx={MORE_ACTIONS_BUTTON_SX}
        >
          Start Manual Assessment
        </Button>
      );
    }
    return (
      <Button
        variant="contained"
        size="small"
        startIcon={autoTaggingIcon}
        onClick={handleOpenAutoTagging}
        sx={MORE_ACTIONS_BUTTON_SX}
      >
        Run Assessment
      </Button>
    );
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      {singleAction ? renderStandaloneAction(singleAction) : null}

      {useMoreActionsMenu ? (
        <>
          <Button
            variant="contained"
            size="small"
            startIcon={<MoreVertIcon sx={{ fontSize: "16px !important" }} />}
            endIcon={
              <KeyboardArrowDownIcon sx={{ fontSize: "16px !important" }} />
            }
            onClick={handleMoreActionsClick}
            sx={MORE_ACTIONS_BUTTON_SX}
          >
            More Actions
          </Button>
          <Menu
            anchorEl={moreActionsAnchorEl}
            open={Boolean(moreActionsAnchorEl)}
            onClose={handleCloseMoreActions}
            PaperProps={{ sx: MORE_ACTIONS_MENU_PAPER_SX }}
          >
            {canShowAddToQueue && (
              <MenuItem onClick={handleAddToQueue} disabled={!canAddToQueue}>
                <ListItemIcon>{addToQueueIcon}</ListItemIcon>
                Add to Assessment Queue
              </MenuItem>
            )}
            {canShowManualAssessment && (
              <MenuItem onClick={handleStartManualAssessment}>
                <ListItemIcon>{manualAssessmentIcon}</ListItemIcon>
                Start Manual Assessment
              </MenuItem>
            )}
            {canShowAutoTagging && (
              <MenuItem onClick={handleOpenAutoTagging}>
                <ListItemIcon>{autoTaggingIcon}</ListItemIcon>
                Run Assessment
              </MenuItem>
            )}
          </Menu>
        </>
      ) : null}

      {canShowAutoTagging && (
        <StartAutoTaggingDialog
          open={autoTaggingOpen}
          onClose={() => setAutoTaggingOpen(false)}
          regulations={autoTaggingRegulations}
        />
      )}

      {(canRunSystemImpact ||
        (canExecuteAutoImpact && canReadChecklist)) && (
        <RunAiAssessmentMenuButton
          companyScopeStarting={companyScopeStarting}
          onCompanyScopeImpact={
            canRunSystemImpact ? onCompanyScopeImpact : undefined
          }
          onCustomScopeImpact={
            canExecuteAutoImpact && canReadChecklist
              ? onCustomScopeImpact
              : undefined
          }
        />
      )}
    </Box>
  );
}
